import {
  PROJECT_JOURNEY_FAIL_REASON_OTHER,
  ProjectJourneyEvent,
  ProjectJourneyEventKind,
  ProjectJourneyFailReason,
  ProjectJourneySnapshot,
  ProjectJourneyUnit,
} from '@lite-workspace/dto-fotoclass-common';

/** RU copy for each normalized pipeline milestone — the contract enum stays copy-free. */
export const PROJECT_JOURNEY_EVENT_LABEL: Record<ProjectJourneyEventKind, string> = {
  [ProjectJourneyEventKind.FILE_UPLOADED]: 'Файл принят',
  [ProjectJourneyEventKind.IMAGE_ANALYZED]: 'Анализ кадра (ML)',
  [ProjectJourneyEventKind.FACE_RECOGNIZED]: 'Распознавание лиц (ML)',
  [ProjectJourneyEventKind.PERSON_CREATED]: 'Персона создана',
  [ProjectJourneyEventKind.SCENE_EMBEDDING]: 'Эмбеддинг сцены',
  [ProjectJourneyEventKind.SCENE_DETECTION_COMPLETED]: 'Детекция сцен по проекту',
  [ProjectJourneyEventKind.PERSONS_CLUSTERING_STARTED]: 'Кластеризация персон',
  [ProjectJourneyEventKind.PERSONS_CLUSTERED]: 'Персоны сведены',
  [ProjectJourneyEventKind.BOOK_ASSEMBLY_STARTED]: 'Сборка альбомов запущена',
  [ProjectJourneyEventKind.BOOK_ASSEMBLY_COMPLETED]: 'Альбомы собраны',
  [ProjectJourneyEventKind.BOOK_ASSEMBLY_FAILED]: 'Сборка упала',
  [ProjectJourneyEventKind.FILE_PROCESS_FAILED]: 'Файл не обработан',
  [ProjectJourneyEventKind.FACE_RECOGNIZE_FAILED]: 'Распознавание лиц упало',
  [ProjectJourneyEventKind.FACE_POST_PROCESS_FAILED]: 'Привязка лица упала',
  [ProjectJourneyEventKind.SCENE_EMBEDDING_FAILED]: 'Эмбеддинг сцены упал',
  [ProjectJourneyEventKind.IMAGE_ANALYZE_FAILED]: 'Анализ кадра (ML) упал',
  [ProjectJourneyEventKind.SCENE_DETECTION_FAILED]: 'Детекция сцен упала',
};

/**
 * RU copy for the canonical fail reasons the contract ships. The keys mirror the orchestrator's
 * allow-list verbatim; an unknown label falls back to its own English text rather than hiding the row.
 */
export const PROJECT_JOURNEY_FAIL_REASON_LABEL: Record<string, string> = {
  'Max retry count reached': 'Исчерпан лимит повторов',
  'No retry strategy available': 'Нет стратегии повтора',
  'Retry strategy failed': 'Стратегия повтора не сработала',
  'Max lifetime exceeded': 'Превышено время жизни процесса',
  'Unsupported source': 'Неподдерживаемый источник',
  'Orphaned: parent already completed': 'Осиротевший процесс: родитель уже завершился',
  'File process incomplete: file information save failed': 'Файл не дообработан: не сохранилась информация о файле',
  'File info save incomplete: one or more file versions failed': 'Информация о файле не сохранена: упали версии файла',
  'File deletion incomplete: one or more zones failed': 'Удаление файла не завершено: упали зоны хранения',
  'Asset process incomplete: asset information save failed': 'Ассет не дообработан: не сохранилась информация',
  'Asset info save incomplete: one or more file versions failed': 'Информация об ассете не сохранена: упали версии',
  [PROJECT_JOURNEY_FAIL_REASON_OTHER]: 'Прочие причины',
};

export function failReasonLabel(reason: string): string {
  return PROJECT_JOURNEY_FAIL_REASON_LABEL[reason] ?? reason;
}

export const PROJECT_JOURNEY_UNIT_TITLE: Record<ProjectJourneyUnit, string> = {
  [ProjectJourneyUnit.PROJECT]: 'Проект',
  [ProjectJourneyUnit.UPLOAD]: 'Загрузка фото',
  [ProjectJourneyUnit.FACES]: 'Лица и персоны',
  [ProjectJourneyUnit.SCENES]: 'Сцены и анализ кадров',
  [ProjectJourneyUnit.CLUSTERING]: 'Сведение персон',
  [ProjectJourneyUnit.ALBUMS]: 'Сборка альбомов',
};

/** Owning services per unit, shown as the small mono subtitle on each flow card. */
export const PROJECT_JOURNEY_UNIT_SERVICES: Record<ProjectJourneyUnit, string> = {
  [ProjectJourneyUnit.PROJECT]: 'api-gateway · orchestrator',
  [ProjectJourneyUnit.UPLOAD]: 'uploader · file-data · image-transform',
  [ProjectJourneyUnit.FACES]: 'face-recognizer · persons',
  [ProjectJourneyUnit.SCENES]: 'scene-data · image-analyzer',
  [ProjectJourneyUnit.CLUSTERING]: 'persons · orchestrator',
  [ProjectJourneyUnit.ALBUMS]: 'book-assembly · books-archiving',
};

/** Left-to-right card order and the flow edges drawn between them. */
export const PROJECT_JOURNEY_UNIT_ORDER: readonly ProjectJourneyUnit[] = [
  ProjectJourneyUnit.PROJECT,
  ProjectJourneyUnit.UPLOAD,
  ProjectJourneyUnit.FACES,
  ProjectJourneyUnit.SCENES,
  ProjectJourneyUnit.CLUSTERING,
  ProjectJourneyUnit.ALBUMS,
];

export const PROJECT_JOURNEY_EDGES: readonly (readonly [ProjectJourneyUnit, ProjectJourneyUnit])[] = [
  [ProjectJourneyUnit.PROJECT, ProjectJourneyUnit.UPLOAD],
  [ProjectJourneyUnit.UPLOAD, ProjectJourneyUnit.FACES],
  [ProjectJourneyUnit.UPLOAD, ProjectJourneyUnit.SCENES],
  [ProjectJourneyUnit.FACES, ProjectJourneyUnit.CLUSTERING],
  [ProjectJourneyUnit.SCENES, ProjectJourneyUnit.CLUSTERING],
  [ProjectJourneyUnit.CLUSTERING, ProjectJourneyUnit.ALBUMS],
];

export interface UnitCounters {
  counts: Partial<Record<ProjectJourneyEventKind, number>>;
  failed: number;
  /** Snapshot-authored breakdown of `failed`; live events only bump the counter, never this list. */
  topFailReasons?: ProjectJourneyFailReason[];
}

export type UnitsState = Record<ProjectJourneyUnit, UnitCounters>;

export function emptyUnitCounters(): UnitCounters {
  return { counts: {}, failed: 0 };
}

export function emptyUnits(): UnitsState {
  const units = {} as UnitsState;
  for (const unit of PROJECT_JOURNEY_UNIT_ORDER) {
    units[unit] = emptyUnitCounters();
  }

  return units;
}

/** Snapshot authority does not cover every unit (e.g. book assembly rides Kafka, not the orchestrator
 *  tree) — a unit absent from the snapshot starts from zero rather than being left undefined. */
export function unitsFromSnapshot(snapshot: ProjectJourneySnapshot): UnitsState {
  const units = emptyUnits();
  for (const unit of snapshot.units) {
    units[unit.unit] = {
      counts: { ...unit.counters },
      failed: unit.failed,
      topFailReasons: unit.topFailReasons ? unit.topFailReasons.map(reason => ({ ...reason })) : undefined,
    };
  }

  return units;
}

/** Units whose counters are backend-computed state, not a tally of hops that passed through. ALBUMS
 *  "completed" means books that currently have a model: re-assembling the same album fires another
 *  completed event without a second album ever existing, so summing would over-count. */
const SNAPSHOT_OWNED_UNITS: ReadonlySet<ProjectJourneyUnit> = new Set<ProjectJourneyUnit>([ProjectJourneyUnit.ALBUMS]);

export function isSnapshotOwnedUnit(unit: ProjectJourneyUnit): boolean {
  return SNAPSHOT_OWNED_UNITS.has(unit);
}

/** Failed events tally separately from their kind's success counter — a failed upload does not
 *  also increment "Файл принят". */
export function applyEventToUnits(units: UnitsState, event: ProjectJourneyEvent): UnitsState {
  if (isSnapshotOwnedUnit(event.unit)) {
    return units;
  }

  const current = units[event.unit] ?? emptyUnitCounters();

  // The breakdown stays as the snapshot authored it — a live increment has no reason text to add,
  // and the next resync brings the reconciled list; dropping it here would blank an open dialog.
  if (event.status === 'failed') {
    return {
      ...units,
      [event.unit]: { counts: current.counts, failed: current.failed + event.count, topFailReasons: current.topFailReasons },
    };
  }

  return {
    ...units,
    [event.unit]: {
      counts: { ...current.counts, [event.kind]: (current.counts[event.kind] ?? 0) + event.count },
      failed: current.failed,
      topFailReasons: current.topFailReasons,
    },
  };
}

export function isEventNewerThanSnapshot(event: ProjectJourneyEvent, generatedAt: string): boolean {
  return new Date(event.ts).getTime() > new Date(generatedAt).getTime();
}

/** The contract carries no event id — this tuple is the only stable identity a feed row has. */
export function journeyEventKey(event: ProjectJourneyEvent): string {
  return `${event.ts}|${event.unit}|${event.kind}|${event.status}|${event.count}|${event.traceId ?? ''}`;
}

/** A history row also covered by a live event (same key) is dropped rather than the live one, since the
 *  live copy may carry a traceId the stored row lacked at emit time. The result is re-sorted rather
 *  than relying on "live is always newer than history": both sides are read at different moments, and
 *  the feed is a timeline the reader scans by time. */
export function mergeJourneyHistoryIntoFeed(
  liveFeed: ProjectJourneyEvent[],
  history: ProjectJourneyEvent[],
): ProjectJourneyEvent[] {
  const seen = new Set(liveFeed.map(journeyEventKey));
  const merged = [...liveFeed];

  for (const event of history) {
    const key = journeyEventKey(event);
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(event);
  }

  return merged.sort((a: ProjectJourneyEvent, b: ProjectJourneyEvent) => new Date(b.ts).getTime() - new Date(a.ts).getTime());
}

export interface ProjectJourneyUnitCounterView {
  kind: ProjectJourneyEventKind;
  label: string;
  value: number;
}

export interface ProjectJourneyFailReasonView {
  reason: string;
  label: string;
  count: number;
}

export interface ProjectJourneyUnitView {
  unit: ProjectJourneyUnit;
  title: string;
  services: string;
  counters: ProjectJourneyUnitCounterView[];
  failed: number;
  /** Empty when the unit carries no breakdown — the card then renders the counter as plain text. */
  failReasons: ProjectJourneyFailReasonView[];
}
